import { Injectable, Injector } from '@angular/core';
import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { Subject } from 'rxjs';
import { internalCdkOverlayRef } from '../cdk-overlay-ref';
import { FILE_PREVIEW_DIALOG_DATA } from '../cdk-overlay-injector';

interface FilePreviewDialogConfig {
  panelClass?: string;
  hasBackdrop?: boolean;
  backdropClass?: string;
  height?: string;
  width?: string;
  data?: any;
}

const DEFAULT_CONFIG: FilePreviewDialogConfig = {
  hasBackdrop: true,
  backdropClass: 'cdk-overlay-transparent-backdrop',
  panelClass: 'tm-file-preview-dialog-panel'
}

@Injectable({
  providedIn: 'root'
})
export class OverlayService {

  constructor(
    private overlay: Overlay,
    private injector: Injector) { }

  _selectedPlatform = new Subject;
  selectedPlatform$ = this._selectedPlatform.asObservable();

  _selectedHosts = new Subject;
  $selectedHosts = this._selectedHosts.asObservable();

  _time = new Subject;
  time$ = this._time.asObservable();

  open(config: FilePreviewDialogConfig, origin, component) {
    // Override default configuration
    const dialogConfig = { ...DEFAULT_CONFIG, ...config };
    // Returns an OverlayRef which is a PortalHost
    const overlayRef = this.createOverlay(dialogConfig, origin);

    const dialogRef = new internalCdkOverlayRef(overlayRef);
    const Injector = this.createInjector(config, dialogRef)
    // Create ComponentPortal that can be attached to a PortalHost
    const filePreviewPortal = new ComponentPortal(component, null, Injector);

    // Attach ComponentPortal to PortalHost
    overlayRef.attach(filePreviewPortal);
    overlayRef.backdropClick().subscribe(() => dialogRef.close())
    return dialogRef;
  }
  private createInjector(config: FilePreviewDialogConfig, dialogRef: internalCdkOverlayRef): PortalInjector {
    // Instantiate new WeakMap for our custom injection tokens
    const injectionTokens = new WeakMap();

    // Set custom injection tokens
    injectionTokens.set(internalCdkOverlayRef, dialogRef);
    injectionTokens.set(FILE_PREVIEW_DIALOG_DATA, config.data);
    // Instantiate new PortalInjector
    return new PortalInjector(this.injector, injectionTokens);
  }

  private createOverlay(config: FilePreviewDialogConfig, origin) {
    const overlayConfig = this.getOverlayConfig(config, origin);
    return this.overlay.create(overlayConfig);
  }

  private getOverlayConfig(config: FilePreviewDialogConfig, origin): OverlayConfig {

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(origin)
      .withPositions([{
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
        panelClass: 'dropdown-container',
      }, {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        panelClass: 'dropdown-container',
      }
      ])
    const overlayConfig = new OverlayConfig({
      hasBackdrop: config.hasBackdrop,
      backdropClass: config.backdropClass,
      panelClass: config.panelClass,
      width: config.width,
      height: config.height,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy
    });

    return overlayConfig;
  }
}
